import { Form, Input, Typography } from 'antd'
import type React from 'react'
import type { CSSProperties } from 'react'
import styled from 'styled-components'
import type { BaseInputProps } from '../../types/types'
import './InputText.less'

const { TextArea } = Input
export type InputTextProps = {
  onChange?: (e?: React.ChangeEvent<HTMLInputElement>) => void
  layout?: 'column' | 'row'
  type?: string | 'email'
  required?: boolean
  errorMessage?: string
  onBlur?: () => void
  id?: string
  element?: 'textarea' | 'input'
  rows?: number
  wrapperClassName?: string
  style?: CSSProperties
  autoSize?: boolean
  maxLength?: number
  formItemMaxWidth?: number
  asNumberInput?: boolean
  tooltip?: string
  label?: string
  helperText?: string
} & Omit<BaseInputProps, 'label'>

function InputText({
  label,
  value,
  onChange,
  placeholder,
  element = 'input',
  disabled,
  layout = 'row',
  rows = 1,
  type,
  required,
  errorMessage,
  onBlur,
  id,
  wrapperClassName,
  style,
  autoSize,
  maxLength = 1000,
  formItemMaxWidth,
  asNumberInput = false,
  tooltip,
  helperText,
  ...props
}: InputTextProps) {
  const labelNode = helperText ? (
    <>
      {label}
      <HelperText>{helperText}</HelperText>
    </>
  ) : (
    label
  )

  const inputContent =
    element === 'input' ? (
      <Input
        id={id}
        placeholder={placeholder as string}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
        onBlur={onBlur}
        {...props}
        style={style}
        maxLength={maxLength}
        // @ts-ignore
        type={asNumberInput && 'number'}
        min={asNumberInput ? 0 : undefined}
      />
    ) : (
      <TextArea
        id={id}
        placeholder={placeholder as string}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
        onBlur={onBlur}
        rows={rows}
        {...props}
        style={style}
        autoSize={autoSize}
        maxLength={maxLength}
      />
    )

  return (
    <div id={type === 'email' ? 'email' : ''}>
      <StyledFormItem
        tooltip={tooltip}
        style={{ maxWidth: formItemMaxWidth || 'inherit' }}
        name={type}
        label={labelNode}
        rules={[
          {
            required,
            ...(type === 'email' && { type }),
            ...(errorMessage && { message: errorMessage })
          }
        ]}
        labelCol={layout === 'column' ? { span: 24 } : {}}
        wrapperCol={layout === 'column' ? { span: 24 } : {}}
        className={wrapperClassName}
        $hasHelper={!!helperText}
      >
        {inputContent}
      </StyledFormItem>
    </div>
  )
}

const HelperText = styled(Typography.Text)`
  width: 100%;
  font-weight: 400 !important;
  font-size: 13px;
  color: ${({ theme }) => theme.color.neutral.color4};
  line-height: 1.4;
  margin-bottom: -12px;
`

const StyledFormItem = styled(Form.Item)<{ $hasHelper?: boolean }>`
  ${({ $hasHelper }) =>
    $hasHelper &&
    `
    .ant-form-item-label {
      overflow: visible;
      white-space: normal;
      height: auto;
      padding-bottom: 0;
    }

    .ant-form-item-label > label {
      height: auto;
      flex-wrap: wrap;
    }
  `}
`

export default InputText
